import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import {
    FLEET_ACTIVE_OPTIONS,
    FLEET_TRAILER_STATUS_OPTIONS,
    FleetTrailer,
    FleetTrailerType
} from '../models/trailer.model';
import { FleetTrailerService } from '../trailer.service';

@Component({
    selector: 'app-trailer-list',
    standalone: true,
    templateUrl: './trailer-list.component.html',
    styleUrls: ['../trailer-page.css'],
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        DatePickerModule,
        InputTextModule,
        SelectModule,
        TableModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class TrailerListComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    rows: FleetTrailer[] = [];
    trailerTypes: FleetTrailerType[] = [];
    isLoading = false;
    totalRecords = 0;
    first = 0;
    rowsPerPage = 10;
    keyword = '';
    active: boolean | null = null;
    status = '';
    trailerTypeId?: number;
    readonly activeOptions = FLEET_ACTIVE_OPTIONS;
    readonly statusOptions = FLEET_TRAILER_STATUS_OPTIONS;
    private activeUpdating = new Set<number>();
    private statusUpdating = new Set<number>();

    constructor(
        private trailerService: FleetTrailerService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Trailers' }];
        this.loadTrailerTypes();
    }

    loadTrailers(event?: TableLazyLoadEvent): void {
        if (event) {
            this.first = event.first ?? 0;
            this.rowsPerPage = event.rows ?? 10;
        }

        const companyId = this.companyId();
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        this.isLoading = true;
        this.trailerService.searchTrailers({
            owningCompanyId: companyId,
            page: Math.floor(this.first / this.rowsPerPage),
            pageSize: this.rowsPerPage,
            keyword: this.keyword,
            active: this.active,
            status: this.status,
            trailerTypeId: this.trailerTypeId
        }).subscribe({
            next: response => {
                this.rows = response.items ?? [];
                this.totalRecords = response.count ?? this.rows.length;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load trailers.' });
            }
        });
    }

    search(): void {
        this.first = 0;
        this.loadTrailers();
    }

    clearSearch(): void {
        this.keyword = '';
        this.active = null;
        this.status = '';
        this.trailerTypeId = undefined;
        this.search();
    }

    create(): void {
        this.router.navigate(['/fleet/trailer/create']);
    }

    view(row: FleetTrailer): void {
        if (row.trailerId) {
            this.router.navigate(['/fleet/trailer/view', row.trailerId]);
        }
    }

    edit(row: FleetTrailer): void {
        if (row.trailerId) {
            this.router.navigate(['/fleet/trailer/edit', row.trailerId]);
        }
    }

    updateActive(row: FleetTrailer, active: boolean): void {
        if (!row.trailerId || row.active === active) {
            return;
        }

        const previous = row.active !== false;
        row.active = active;
        this.activeUpdating.add(row.trailerId);
        this.trailerService.updateTrailerActive(row.trailerId, active).subscribe({
            next: response => {
                row.active = response.data?.active ?? active;
                this.activeUpdating.delete(row.trailerId!);
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Trailer marked ${active ? 'active' : 'inactive'}.` });
            },
            error: () => {
                row.active = previous;
                this.activeUpdating.delete(row.trailerId!);
                this.messageService.add({ severity: 'error', summary: 'Update failed', detail: 'Could not update trailer active flag.' });
            }
        });
    }

    updateStatus(row: FleetTrailer, status: string): void {
        if (!row.trailerId || row.status === status) {
            return;
        }

        const previous = row.status;
        row.status = status;
        this.statusUpdating.add(row.trailerId);
        this.trailerService.updateTrailerStatus(row.trailerId, status).subscribe({
            next: response => {
                row.status = response.data?.status ?? status;
                this.statusUpdating.delete(row.trailerId!);
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Trailer status updated.' });
            },
            error: () => {
                row.status = previous;
                this.statusUpdating.delete(row.trailerId!);
                this.messageService.add({ severity: 'error', summary: 'Update failed', detail: 'Could not update trailer status.' });
            }
        });
    }

    metrics(): PageMetric[] {
        return [
            { label: 'Trailers', value: this.totalRecords, hint: 'Matching trailer assets', tone: 'info' },
            { label: 'Loaded', value: this.rows.length, hint: 'Rows in current page', tone: 'default' },
            { label: 'Available', value: this.rows.filter(row => row.status === 'AVAILABLE').length, hint: 'Visible available assets', tone: 'success' },
            { label: 'Maintenance', value: this.rows.filter(row => row.status === 'MAINTENANCE' || row.status === 'OUT_OF_SERVICE').length, hint: 'Visible unavailable assets', tone: 'warn' }
        ];
    }

    activeFilterCount(): number {
        return [this.keyword, this.status, this.trailerTypeId, this.active === null ? '' : 'active'].filter(Boolean).length;
    }

    activeSeverity(row: FleetTrailer): 'success' | 'danger' {
        return row.active === false ? 'danger' : 'success';
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch (status) {
            case 'AVAILABLE':
                return 'success';
            case 'ASSIGNED':
                return 'info';
            case 'MAINTENANCE':
                return 'warning';
            case 'OUT_OF_SERVICE':
            case 'INACTIVE':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    isActiveUpdating(row: FleetTrailer): boolean {
        return !!row.trailerId && this.activeUpdating.has(row.trailerId);
    }

    isStatusUpdating(row: FleetTrailer): boolean {
        return !!row.trailerId && this.statusUpdating.has(row.trailerId);
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    private loadTrailerTypes(): void {
        const companyId = this.companyId();
        if (!companyId) {
            return;
        }
        this.trailerService.searchTrailerTypes({ owningCompanyId: companyId, page: 0, pageSize: 250, active: true })
            .subscribe({
                next: response => this.trailerTypes = response.items ?? [],
                error: () => this.messageService.add({ severity: 'warn', summary: 'Trailer types', detail: 'Could not load trailer type filters.' })
            });
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }
}
